import * as React from 'react';
import { MapMouseEvent, useMap } from 'react-map-gl/mapbox';

import { Mouse } from '@longline/aqua-ui/controls/Mouse';
import { Key } from '@longline/aqua-ui/controls/Key';

import { PolygonEditorSurface } from './PolygonEditorSurface';
import { PolygonEditorPoints } from './PolygonEditorPoints';
import { PolygonEditorExtenders } from './PolygonEditorExtenders';
import { PolygonEditorScaler } from './PolygonEditorScaler';
import { IPoint } from '../../types/IPoint';
import { Guide } from '../../ui/Guide';

interface IPolygonEditorProps {
  /** 
   * Points of polygon being drawn. The first point must not be duplicated
   * as the last point.
   */
  points: IPoint[];
  /** Fired when polygon's points change. */
  onChange: (points: IPoint[]) => void;
  /** Fired when polygon is deleted. */
  onDelete: () => void;
  /** Fired when drawing mode is canceled. */
  onCancel: () => void;
  /** Can polygon be scaled? This will add a scaling box around the polygon. */
  allowScaling?: boolean;
}

const PolygonEditor = (props: IPolygonEditorProps) => {
  const map = useMap().current!;

  const [points, setPoints] = React.useState(props.points);

  React.useEffect(() => {
    // Only update if points actually differ (avoid feedback loop during drag)
    const pointsEqual = points.length === props.points.length &&
      points.every((p, i) => p.lat === props.points[i].lat && p.lng === props.points[i].lng);
    if (!pointsEqual) {
      setPoints([...props.points]);
    }
  }, [props.points]);

  const handleKeydown = (e: KeyboardEvent) => {
    switch(e.code) {
      case 'Escape':
      case 'Backspace':
        map.getCanvas().style.cursor = '';
        props.onCancel();
        break;
      case 'Delete':
      case 'NumpadDecimal':
        if(!e.defaultPrevented) props.onDelete();
        break;        
    }
  }   

  const handleClick = (e: MapMouseEvent) => {
    // Shift+click starts box-select, don't interfere
    if (e.originalEvent.shiftKey) return;

    // Ctrl+click on the selected structure deselects it
    if(e.defaultPrevented) {
      const additive = e.originalEvent.ctrlKey || e.originalEvent.metaKey;
      if (additive) {
        props.onCancel();
      }
      return;
    }
    props.onCancel();
  }

  const mount = () => {
    // Start listening to keyboard:
    document.addEventListener('keydown', handleKeydown);            
    map.on('click', handleClick);
  }

  const unmount = () => {
    // Stop listening to keyboard:
    document.removeEventListener('keydown', handleKeydown);            
    map.off('click', handleClick);
  }

  // Mounting/unmounting must only happen once:
  React.useEffect(() => {
    mount();
    return unmount
  }, []);

  const handleChange = (newPoints: IPoint[]) => {
    setPoints(newPoints);
    if(newPoints.length < 3) {
      props.onDelete();
    } else {
      props.onChange(newPoints);
    }
  }

  return (
    <>
      <Guide x={16} y={64} title="Editing a polygon structure">
        <ul>
          <li>Click <Mouse size={14} left/> and drag the big dots to adjust the structure's vertex positions.</li>
          <li>Click <Mouse size={14} left/> the small dots in the middle of the lines to add vertices.</li>
          <li>Click <Mouse size={14} left/> and drag the polygon surface to move the structure.</li>
          <li>With a vertex selected, press <Key value="DEL"/> to remove a vertex.</li>
          <li><Key value="ESC"/> cancels.</li>
          <li><Key value="CTRL"/> <Key value="C"/>, <Key value="V"/> copies.</li>
          <li>Use <Key value="CTRL"/><Mouse size={14} left/> to multi-select.</li>
        </ul>
      </Guide>    
      <PolygonEditorSurface   points={points} onChange={handleChange} />
      <PolygonEditorPoints    points={points} onChange={handleChange}/>
      <PolygonEditorExtenders points={points} onChange={handleChange}/>
      {props.allowScaling && <PolygonEditorScaler points={points} onChange={handleChange}/>}
    </>
  );
}

export { PolygonEditor, IPolygonEditorProps }
